<script setup lang="ts">
import { computed, onMounted, onUnmounted, provide, reactive, ref, watch } from "vue";
import { captureException } from "@sentry/vue";
import { useRoute, useRouter } from "vue-router";
import useVuelidate from "@vuelidate/core";
import z from "zod";

import { throttle, isTokenInvalid, parseQueryQuoteNumber } from "@/utils";
import { getPartialFromRequest, insertPartialFormRequest } from "@/data/apply";
import { mapDataFromQuoteRequest, mapDataToPartialForm } from "@/utils/quote";
import { scrollErrorIntoView } from "@/composables/scrollErrorIntoView";
import { ToastRenderer, useToast } from "@/services/toast";
import { runQuote } from "@/composables/runQuote";
import { AccordionStatus, AccordionId } from "@/stores/mainView";
import { getStores } from "@/stores";
import {
    ImportantInformation,
    AboutYou,
    YourDetails,
    DependantAboutYou,
    DependantDailyActivities,
    PensionOptions,
    DailyActivities,
    AccordionItem,
    TimeoutWarning,
    Finally,
    GeneratingQuotesModal,
    Modal,
} from "@/components";
import { QuoteToken } from "@/stores/quotes";
import { formNavigationKey } from "@/components/keys";

const { toast } = useToast();

const {
    pensionOptionsStore,
    dependantDailyActivitiesStore,
    dailyActivitiesStore,
    sessionTimeoutStore,
    yourDetailsStore,
    quoteStore,
    config,
} = getStores();

const queryQuoteNumber = computed(() => parseQueryQuoteNumber(route.query?.quotenumber));
const v$ = useVuelidate();

const firstAccordionId: AccordionId = AccordionId.ImportantInformation;
const finalAccordionId: AccordionId = AccordionId.Finally;

const router = useRouter();
const route = useRoute();

type AccordionState = {
    locked: boolean;
    status: AccordionStatus;
}

const accordionState = ref<Record<AccordionId, AccordionState>>({
    [AccordionId.ImportantInformation]: { locked: false, status: AccordionStatus.Incomplete },
    [AccordionId.YourDetails]: { locked: true, status: AccordionStatus.Incomplete },
    [AccordionId.PensionOptions]: { locked: true, status: AccordionStatus.Incomplete },
    [AccordionId.AboutYou]: { locked: true, status: AccordionStatus.Incomplete },
    [AccordionId.DailyActivities]: { locked: true, status: AccordionStatus.Incomplete },
    [AccordionId.DependantAboutYou]: { locked: true, status: AccordionStatus.Incomplete },
    [AccordionId.DependantDailyActivities]: { locked: true, status: AccordionStatus.Incomplete },
    [AccordionId.Finally]: { locked: true, status: AccordionStatus.Incomplete },
});

type Accordion = {
    id: AccordionId;
    label: string;
    ctaText: string;
    subheading?: string;
    state: AccordionState;
    component: typeof ImportantInformation
        | typeof YourDetails
        | typeof PensionOptions
        | typeof AboutYou
        | typeof DependantAboutYou
        | typeof DailyActivities
        | typeof DependantDailyActivities
        | typeof Finally;

};

const accordions = computed<Accordion[]>(() => {
    const accordions: Accordion[] = [
        {
            id: AccordionId.ImportantInformation,
            label: "Important information before you begin",
            ctaText: "Let's get started",
            component: ImportantInformation,
            state: accordionState.value[AccordionId.ImportantInformation],
        },
        {
            id: AccordionId.YourDetails,
            label: "Your details",
            subheading: "This section will take approximately 4 minutes",
            ctaText: "Agree and continue",
            component: YourDetails,
            state: accordionState.value[AccordionId.YourDetails],
        },
        {
            id: AccordionId.PensionOptions,
            label: "Your pension options - select what you need",
            subheading: "This section will take approximately 8 minutes",
            ctaText: "Save and continue",
            component: PensionOptions,
            state: accordionState.value[AccordionId.PensionOptions],
        },
        {
            id: AccordionId.AboutYou,
            label: "About you",
            subheading: `This section will take approximately
        4 minutes to complete or 9 minutes if you're adding a dependant.`,
            ctaText: "Save and continue",
            component: AboutYou,
            state: accordionState.value[AccordionId.AboutYou],
        },
        ...pensionOptionsStore.spouseDependant
            ? [{
                id: AccordionId.DependantAboutYou,
                label: "About your dependant",
                ctaText: "Save and continue",
                component: DependantAboutYou,
                state: accordionState.value[AccordionId.DependantAboutYou],
            }]
            : [],
        ...dailyActivitiesStore.dailyActivitiesRequired
            ? [{
                id: AccordionId.DailyActivities,
                label: "Your daily activities",
                ctaText: "Save and continue",
                component: DailyActivities,
                state: accordionState.value[AccordionId.DailyActivities],
            }]
            : [],
        ...dependantDailyActivitiesStore.dependantDailyActivitiesRequired
            ? [{
                id: AccordionId.DependantDailyActivities,
                label: "Your spouse / dependant daily activities",
                ctaText: "Save and continue",
                component: DependantDailyActivities,
                state: accordionState.value[AccordionId.DependantDailyActivities],
            }]
            : [],
        {
            id: AccordionId.Finally,
            label: "Finally",
            subheading: "This section will take approximately 1 minute",
            ctaText: "Agree and continue",
            component: Finally,
            state: accordionState.value[AccordionId.Finally],
        },
    ];
    return accordions;
});

const state = reactive<{
    currentAccordionId: AccordionId | null;
    accordionToValidate: AccordionId | null;
    quoteToken: QuoteToken | null;
    showQuotingOverlay: boolean;
    showMyLastQuoteModal: boolean;
    preventFormNavigation: boolean;
    preventFormNavigationMessage: string | null;
}>({
    currentAccordionId: null,
    accordionToValidate: null,
    showQuotingOverlay: false,
    quoteToken: null,
    showMyLastQuoteModal: false,
    preventFormNavigation: false,
    preventFormNavigationMessage: null,
});

onMounted(async () => {
    setupSessionTimeout();
    state.quoteToken = quoteStore.getLatestQuoteToken();

    const editingExistingQuote = !!queryQuoteNumber.value;
    const form = sessionStorage.getItem("form");
    let loadedSuccessfully = false;

    if (editingExistingQuote) {
        loadedSuccessfully = await loadExistingQuote(queryQuoteNumber.value);
    }
    if (form !== null && (!editingExistingQuote || !loadedSuccessfully)) {
        loadedSuccessfully = await loadPartialForm(form);
    }

    if (!loadedSuccessfully) {
        state.currentAccordionId = firstAccordionId;
    }
});

onUnmounted(() => {
    resetSessionTimerEvents.forEach(event => {
        document.removeEventListener(event, resetTimeoutHandler);
    });
    clearInterval(intervalTimer);
});

watch(() => sessionTimeoutStore.activeOverlay, (activeOverlay) => {
    if (activeOverlay) {
        document.body.classList.add("stop-body-scroll");
    } else {
        document.body.classList.remove("stop-body-scroll");
    }
});

watch(() => sessionTimeoutStore.currentTime, () => {
    if (sessionTimeoutStore.currentTime > sessionTimeoutStore.endTime) {
        sessionTimeoutStore.resetSession();
        router.push({ name: "session_timeout" });
    }
});

const redirectToMyLastQuote = () => {
    if (!state.quoteToken) {
        return;
    }
    router.push({ name: "results", query: { quotenumber: state.quoteToken.quoteNumber } });
};

async function loadExistingQuote(quoteNumber: string): Promise<boolean> {
    if (!yourDetailsStore.quoteLoaded) {
        const token = quoteStore.latestTokenByQuoteNumber(quoteNumber);
        await quoteStore.getQuote(token);

        if (quoteStore.quote) {
            mapDataFromQuoteRequest(quoteStore.quote);
        } else {
            return false;
        }
    }

    await validateForm();

    return true;
}

async function loadPartialForm(form: string): Promise<boolean> {
    try {
        form = JSON.parse(form);
    } catch (err) {
        captureException(err);
        sessionStorage.removeItem("form");
        return false;
    }

    const maybeForm = z.object({
        id: z.number(),
        token: z.string(),
    }).safeParse(form);

    if (!maybeForm.success) {
        captureException(maybeForm.error);
        sessionStorage.removeItem("form");
        return false;
    }

    const { id, token } = maybeForm.data;

    if (isTokenInvalid(token)) {
        sessionStorage.removeItem("form");
        return false;
    }

    // this should not need to be inside the try/catch
    // change get to a safe parse
    // change the map data to not throw
    try {
        const formData = await getPartialFromRequest(id, token);
        if (formData) {
            mapDataFromQuoteRequest(formData);
        }
    } catch (err) {
        captureException(err);
        showErrorToast("We could not retrieve quotes at this time");
        return false;
    }

    await validateForm();

    return true;
}

const validateForm = async (showClosedAccordionErrors = false): Promise<boolean> => {
    const enabledAccordions = accordions.value.map(acc => acc.id);
    state.currentAccordionId = null;

    for (const id of enabledAccordions) {
        const valid = await validateAccordion(id, showClosedAccordionErrors);
        if (valid) {
            accordionState.value[id].status = AccordionStatus.Success;

            const nextAccordionId = getNextAccordionId(id);
            if (nextAccordionId) {
                accordionState.value[nextAccordionId].locked = false;
            }
        } else {
            return false;
        }
    }

    state.currentAccordionId = finalAccordionId;

    return true;
};

const validateAccordion = async (id: AccordionId, showClosedAccordionErrors = false): Promise<boolean> => {
    state.accordionToValidate = id;
    const isValid = await v$.value.$validate();

    if (!isValid) {
        if (state.currentAccordionId !== id) {
            state.currentAccordionId = id;
        }
        accordionState.value[id].status = AccordionStatus.Error;
        if (showClosedAccordionErrors) {
            await v$.value.$validate();
        }
        scrollErrorIntoView();
        state.accordionToValidate = null;

        return false;
    }

    state.accordionToValidate = null;

    return true;
};

const getNextAccordionId = (currentAccordionId?: AccordionId): AccordionId | null => {
    if (!currentAccordionId) {
        currentAccordionId = state.currentAccordionId ?? undefined;
    }

    if (currentAccordionId === finalAccordionId || !currentAccordionId) {
        return null;
    }

    const nextAccordionIndex = accordions.value.findIndex(acc => acc.id === currentAccordionId) + 1;
    const nextAccordionId = accordions.value[nextAccordionIndex].id;
    return nextAccordionId;
};

async function handleAccordionCta(id: AccordionId): Promise<void> {
    if (state.preventFormNavigation) {
        showFormNavigationBlockedToast(state.preventFormNavigationMessage);
        return;
    }

    if (id === firstAccordionId) {
        return completeAccordion(id);
    }

    const canQuote = await saveAccordion(id);
    if (id === finalAccordionId && canQuote) {
        completeForm();
    }
}

function completeAccordion(id: AccordionId): void {
    const nextAccordionId = getNextAccordionId(id);

    if (!nextAccordionId) {
        return;
    }

    accordionState.value[id].status = AccordionStatus.Success;
    accordionState.value[nextAccordionId].locked = false;

    state.currentAccordionId = nextAccordionId;

    emitSessionTracingEvent({
        section: accordionIdToSectionNameMap.get(nextAccordionId) ?? "unknown",
    });
    document.getElementById(`accordion_${id}`)?.scrollIntoView({ block: "start" });
}

async function saveAccordion(id: AccordionId): Promise<boolean> {
    const accordionValid = await validateAccordion(id);
    if (!accordionValid) {
        return false;
    }

    const partialFormRequest = mapDataToPartialForm();

    partialFormRequest.affiliate = quoteStore.getAffiliate();

    try {
        const data = await insertPartialFormRequest(partialFormRequest);
        sessionStorage.setItem("form", JSON.stringify({ id: data.partialId, token: data.token }));
    } catch (err) {
        handleFailedSave(err, "We could not save your details at this time");
        return false;
    }

    completeAccordion(id);
    return true;
}

async function completeForm(): Promise<void> {
    state.showQuotingOverlay = true;

    const isValid = await validateForm(true); // show ClosedAccordionErrors

    if (!isValid) {
        state.showQuotingOverlay = false;
        return;
    }

    try {
        const { quoteNumber } = await runQuote(queryQuoteNumber.value);
        await router.push({ name: "results", query: { quotenumber: quoteNumber } });
    } catch (err) {
        state.showQuotingOverlay = false;
        handleFailedSave(err, "We could not retrieve quotes at this time");
    }
}

async function handleFailedSave(err: unknown, message: string): Promise<void> {
    captureException(err);

    const isValid = await validateForm(true); // show ClosedAccordionErrors

    if (isValid) {
        showErrorToast(message);
    }
}

function showErrorToast(title: string) {
    toast({
        useUnsafeHTMLMessage: true,
        title,
        message:
            `Please try again or call Annuity Ready on <a href='tel:08003265479'>0800 326 5479</a> Monday - Friday,
                between 9-5, excluding Bank Holidays.`,
        timeout: config.toastTimeout,
    });
}

const resetTimeoutHandler = throttle(2000, () => {
    if (!sessionTimeoutStore.showTimeoutWarning()) {
        sessionTimeoutStore.touchSession();
    }
});

const resetSessionTimerEvents = ["click", "focusin", "keydown", "scroll"];
let intervalTimer: NodeJS.Timeout;

async function setupSessionTimeout() {
    resetSessionTimerEvents.forEach(event => {
        document.addEventListener(event, resetTimeoutHandler);
    });

    intervalTimer = setInterval(
        () => {
            sessionTimeoutStore.currentTime = Number(new Date());
        },
        100,
    );
}

function closeOverlay() {
    sessionTimeoutStore.activeOverlay = false;
}

const accordionIdToSectionNameMap = new Map<AccordionId, string>([
    [AccordionId.ImportantInformation, "important-information"],
    [AccordionId.YourDetails, "your-details"],
    [AccordionId.PensionOptions, "pension-options"],
    [AccordionId.AboutYou, "about-you"],
    [AccordionId.DependantAboutYou, "dependant-about-you"],
    [AccordionId.DailyActivities, "daily-activities"],
    [AccordionId.DependantDailyActivities, "dependant-daily-activities"],
    [AccordionId.Finally, "finally"]]);

async function toggleAccordion(id: AccordionId) {
    if (state.preventFormNavigation) {
        showFormNavigationBlockedToast(state.preventFormNavigationMessage);
        return;
    }
    if (state.currentAccordionId !== null) {
        await validateAccordion(state.currentAccordionId, true);
    }
    if (state.currentAccordionId === id) {
        return; // don't close the currently open accordion
    }
    emitSessionTracingEvent({
        section: accordionIdToSectionNameMap.get(id) ?? "unknown",
    });
    state.currentAccordionId = id;
}

function emitSessionTracingEvent(input: Record<string, string>) {
    window._detector?.triggerCustomEventMap(
        "form_section_change",
        input,
    );
}

// ============================================================================
// Handle blocking navigation on medical forms with unsaved data

function showFormNavigationBlockedToast(message: string | null) {
    toast({
        useUnsafeHTMLMessage: true,
        title: "Warning",
        message: message ?? "You have unsaved changes.",
        timeout: config.toastTimeout,
    });
}

const disableFormNavigation = (message: string | null) => {
    state.preventFormNavigation = true;
    state.preventFormNavigationMessage = message;
};

const enableFormNavigation = () => {
    state.preventFormNavigation = false;
    state.preventFormNavigationMessage = null;
};

provide(formNavigationKey, {
    enable: enableFormNavigation,
    disable: disableFormNavigation,
});

// ============================================================================

</script>

<template>
    <div>
        <div
            :class="{
                'overlay': sessionTimeoutStore.showTimeoutWarning() || state.showQuotingOverlay
            }"
            @click="closeOverlay()"
        />
        <TimeoutWarning
            v-if="sessionTimeoutStore.shouldShowTimeoutWarning()"
            :time-left="sessionTimeoutStore.getTimeLeft()"
        />
        <GeneratingQuotesModal v-if="state.showQuotingOverlay" />
        <button
            v-if="!!state.quoteToken"
            id="my-last-quote-button"
            type="button"
            class="btn btn-primary float-sm-end ml-0 ml-sm-2 mt-3 mt-sm-2 mb-0 mb-sm-2"
            @click="state.showMyLastQuoteModal=true"
        >
            My last quote
        </button>
        <Modal
            id="my-last-quote-modal"
            :show="state.showMyLastQuoteModal"
            header="Warning: Possible unsaved data"
            :cross="false"
        >
            <template #body>
                <p class="m-2">
                    If you've entered any new information into the quote form, you might lose it if you view your old quotes
                </p>
                <button
                    type="button"
                    class="btn btn-warning m-2"
                    @click="redirectToMyLastQuote()"
                >
                    View Previous Quote Anyway
                </button>
                <button
                    type="button"
                    class="btn btn-alert m-2"
                    @click="state.showMyLastQuoteModal=false"
                >
                    Cancel
                </button>
            </template>
        </Modal>
        <div
            id="AR-accordion"
            class="accordion"
        >
            <AccordionItem
                v-for="(accordion, index) in accordions"
                :id="accordion.id"
                :key="accordion.id"
                :label="accordion.label"
                :subheading="accordion?.subheading"
                :cta-text="accordion.ctaText"
                :label-index="index+1"
                :open="state.currentAccordionId === accordion.id"
                :locked="accordion.state.locked"
                :status="accordion.state.status"
                :keep-rendered="state.accordionToValidate === accordion.id"
                @cta-clicked="handleAccordionCta"
                @toggle-accordion="toggleAccordion"
            >
                <component :is="accordion.component" />
            </AccordionItem>
        </div>
    </div>
    <ToastRenderer />
</template>
